/**
 * Client for the platform ("god mode") console API.
 *
 * This is the ONLY place in src/ that knows the /api/platform/* paths.
 *
 * Note that nothing here is a security boundary — the console's UI gate
 * (`isPlatformAdmin` on AuthContext) only decides what to render. Every one of
 * these requests is independently authorized server-side in
 * lib/platformAuth.js against the platform_admins table.
 */

import { getSupabaseClient, hasSupabaseConfig } from './supabaseClient';

export class PlatformApiError extends Error {
  constructor({ status, code, message, requestId }) {
    super(message || 'Request failed');
    this.name = 'PlatformApiError';
    this.status = status;
    this.code = code;
    this.requestId = requestId || null;
  }
}

async function accessToken() {
  if (!hasSupabaseConfig()) return null;
  const supabase = getSupabaseClient();
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token || null;
}

async function platformFetch(path, { method = 'GET', body, signal } = {}) {
  const token = await accessToken();
  if (!token) {
    throw new PlatformApiError({ status: 401, code: 'NO_SESSION', message: 'Not signed in' });
  }

  const res = await fetch(path, {
    method,
    signal,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new PlatformApiError({
      status: res.status,
      code: json.code || 'ERROR',
      // A 404 here means "you are not a platform admin" — the server
      // deliberately does not confirm the endpoint exists to a prober.
      message: res.status === 404 ? 'Platform console not available' : json.error,
      requestId: json.requestId,
    });
  }
  return json;
}

function qs(params = {}) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    search.set(key, String(value));
  });
  const str = search.toString();
  return str ? `?${str}` : '';
}

export function getOverview({ days = 30, only, signal } = {}) {
  return platformFetch(`/api/platform/overview${qs({ days, only })}`, { signal });
}

export function listResource(resource, params = {}, { signal } = {}) {
  return platformFetch(`/api/platform/list${qs({ resource, ...params })}`, { signal });
}

export function getDetail(resource, id, params = {}, { signal } = {}) {
  return platformFetch(`/api/platform/detail${qs({ resource, id, ...params })}`, { signal });
}

export function runAction(action, payload = {}) {
  return platformFetch('/api/platform/action', { method: 'POST', body: { action, ...payload } });
}
